/**
 * src/gm/kernel/wakeup-queue.ts — 唤醒队列（设计稿 §4：Paperclip 状态机形状）。
 *
 * 玩家意图一律入队，队列持久化（tp3:wakeup 单键数组）：
 *   pending ──claim──► claimed ──执行──► completed
 *      │重复入队           │执行中发现前置已变化
 *      └──► coalesced      └──► deferred（队列空转时重估：合法回 pending，非法 failed）
 *   claimed 卡死（崩溃）→ 恢复时按当前存档重估（§6 ②）
 *
 * 语义：入队即校验（非法动作当场拒绝）；合并（同项目同动作 pending/claimed 已存在 →
 * 新请求 coalesced 并入，双击安全）；终态记录保留上限 WAKEUP_TERMINAL_CAP。
 */

import { WAKEUP_KEY, WAKEUP_TERMINAL_CAP } from '../platform/config'
import { readJson, writeJson } from '../platform/storage'

export type WakeupAction =
  | 'startPlanning'
  | 'startDevelopment'
  | 'publish'
  | 'abandon'
  | 'accept' // 系统内部：恢复路径补验收
  | 'publishDefective' // 事故三选之一：残次品上架（见 game-master 注释：Phase A 对齐必需）

export type WakeupSource = 'player' | 'resume' | 'system'
export type WakeupState = 'pending' | 'claimed' | 'coalesced' | 'deferred' | 'completed' | 'failed'

export interface WakeupRequest {
  id: string // wk_<timestamp>_<rand>
  projectId: string
  action: WakeupAction
  source: WakeupSource
  state: WakeupState
  createdAt: string
  detail?: unknown // 失败原因 / 合并信息
}

const TERMINAL: WakeupState[] = ['completed', 'failed', 'coalesced']

export class WakeupQueue {
  /** 状态迁移回调（内核注入：产 wakeup 事件） */
  constructor(private onTransition: (req: WakeupRequest, transition: string) => void) {}

  list(projectId?: string): WakeupRequest[] {
    const all = readJson<WakeupRequest[]>(WAKEUP_KEY) ?? []
    return projectId ? all.filter((r) => r.projectId === projectId) : all
  }

  private persistAll(all: WakeupRequest[]): void {
    // 终态记录滚动裁剪（保留最近 WAKEUP_TERMINAL_CAP 条）
    const active = all.filter((r) => !TERMINAL.includes(r.state))
    const terminal = all.filter((r) => TERMINAL.includes(r.state))
    const trimmed = terminal.slice(-WAKEUP_TERMINAL_CAP)
    writeJson(WAKEUP_KEY, [...active, ...trimmed])
  }

  private update(id: string, fn: (r: WakeupRequest) => WakeupRequest): WakeupRequest | null {
    const all = readJson<WakeupRequest[]>(WAKEUP_KEY) ?? []
    const idx = all.findIndex((r) => r.id === id)
    if (idx < 0) return null
    all[idx] = fn(all[idx])
    this.persistAll(all)
    return all[idx]
  }

  /** 同项目同动作的 pending/claimed 请求（合并判定） */
  findDuplicate(projectId: string, action: WakeupAction): WakeupRequest | undefined {
    return this.list(projectId).find(
      (r) => r.action === action && (r.state === 'pending' || r.state === 'claimed'),
    )
  }

  /**
   * 入队。先合并判定（重复意图 → 新记录标记 coalesced 并入，返回既有请求），
   * 再入队即校验（validate 抛错 = 非法动作当场拒绝，不产生垃圾请求）。
   */
  enqueue(
    action: WakeupAction,
    projectId: string,
    source: WakeupSource,
    validate: () => void,
  ): { request: WakeupRequest; isNew: boolean } {
    const dup = this.findDuplicate(projectId, action)
    if (dup) {
      const merged: WakeupRequest = {
        id: newId(),
        projectId,
        action,
        source,
        state: 'coalesced',
        createdAt: new Date().toISOString(),
        detail: { into: dup.id },
      }
      const all = readJson<WakeupRequest[]>(WAKEUP_KEY) ?? []
      all.push(merged)
      this.persistAll(all)
      this.onTransition(merged, 'coalesced')
      return { request: dup, isNew: false }
    }
    validate()
    const req: WakeupRequest = {
      id: newId(),
      projectId,
      action,
      source,
      state: 'pending',
      createdAt: new Date().toISOString(),
    }
    const all = readJson<WakeupRequest[]>(WAKEUP_KEY) ?? []
    all.push(req)
    this.persistAll(all)
    this.onTransition(req, 'pending')
    return { request: req, isNew: true }
  }

  nextPending(projectId: string): WakeupRequest | undefined {
    return this.list(projectId).find((r) => r.state === 'pending')
  }

  transition(id: string, state: WakeupState, detail?: unknown): WakeupRequest | null {
    const updated = this.update(id, (r) => ({
      ...r,
      state,
      ...(detail !== undefined ? { detail } : {}),
    }))
    if (updated) this.onTransition(updated, state)
    return updated
  }

  /** 恢复/空转重估对象：deferred + 卡死的 claimed（崩溃 mid-execution） */
  stuckRequests(projectId?: string): WakeupRequest[] {
    return this.list(projectId).filter((r) => r.state === 'deferred' || r.state === 'claimed')
  }
}

function newId(): string {
  return `wk_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
}
